import {useMemo} from 'react'
import type {ReactElement, ReactNode} from 'react'
import type {
	Elements,
	ElementModels,
	IContentItem,
} from '@kontent-ai/delivery-sdk'
import {
	nodeParse,
	transformToPortableText,
} from '@kontent-ai/rich-text-resolver'
import {PortableText} from '@portabletext/react'
import {
	HeaderCard,
	ALLOWED_HEADING_TAGS,
} from '@/products/flexible-web-toolkit/components/header-card'
import type {HeaderCardTitleTag} from '@/products/flexible-web-toolkit/components/header-card'
import {useTrustArcCookieValue} from '@/utilities/trustarc'
import type {Block} from '@/services/kontent-ai/types'
import {
	RichTextRenderer,
	SubMark,
	SupMark,
	ExternalLinkMark,
	InternalLinkMark,
} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import {FullscreenVideo} from '@/products/flexible-web-toolkit/components/full-screen-video'
import type {CtaButtonContentItem} from '../cta-button'
import {ReusableCTAButtonComponent} from '../cta-button'

export type HomePageHeaderBlockContentItem = IContentItem<{
	title: Elements.RichTextElement
	titleTag: Elements.MultipleChoiceElement
	text: Elements.RichTextElement
	videoId: Elements.TextElement
	backgroundImages: Elements.AssetsElement
	buttons: Elements.LinkedItemsElement<CtaButtonContentItem>
	backgroundColour: Elements.TextElement
	mobileImage: Elements.AssetsElement
}>

const InlineBlock = ({children}: {children?: ReactNode}): ReactElement => (
	<>{children}</>
)

export const HomePageHeaderBlockBlock: Block<
	HomePageHeaderBlockContentItem
> = ({block, ...context}) => {
	const trustArcCookie = useTrustArcCookieValue()
	const hideVideo = trustArcCookie === '0:' ? 'hide' : ''

	const titleTagOptions = block.elements.titleTag as
		| ElementModels.MultipleChoiceOption[]
		| undefined
	const selectedCodename = titleTagOptions?.[0]?.codename
	const selectedTag: HeaderCardTitleTag = ALLOWED_HEADING_TAGS.includes(
		selectedCodename as HeaderCardTitleTag
	)
		? (selectedCodename as HeaderCardTitleTag)
		: 'h1'

	const desktopImage =
		block.elements.backgroundImages[0] ??
		block.elements.mobileImage[0] ??
		null
	const mobileImage = block.elements.mobileImage[0] ?? desktopImage

	const titleNode = useMemo(
		(): ReactElement => (
			<PortableText
				components={{
					block: {
						normal: InlineBlock,
						h1: InlineBlock,
						h2: InlineBlock,
						h3: InlineBlock,
						h4: InlineBlock,
						h5: InlineBlock,
						h6: InlineBlock,
					},
					types: {
						image: () => null,
						component: () => null,
						table: () => null,
					},
					marks: {
						sub: SubMark,
						sup: SupMark,
						link: ExternalLinkMark,
						internalLink: InternalLinkMark,
					},
				}}
				value={transformToPortableText(
					nodeParse(block.elements.title.value)
				)}
			/>
		),
		[block.elements.title.value]
	)

	return (
		<div>
			<FullscreenVideo
				backgroundColor={block.elements.backgroundColour}
				controls={1}
				id="header"
				mobilePosterFrameImageUrl={mobileImage?.url ?? ''}
				posterFrameAlignment="top"
				posterFrameImageAltText={desktopImage?.description ?? ''}
				posterFrameImageUrl={desktopImage?.url ?? ''}
				priority
				videoId={hideVideo === 'hide' ? '' : block.elements.videoId}
			>
				<HeaderCard
					key={block.elements.videoId}
					title={titleNode}
					titleTag={selectedTag}
				>
					<RichTextRenderer
						className="mb-4 mt-3"
						element={block.elements.text}
						removeEmptyLines
						{...context}
					/>
					{block.elements.buttons[0] ? (
						<div className="mt-10">
							{block.elements.buttons.map((button) => (
								<ReusableCTAButtonComponent
									block={button}
									className="mr-6 mt-6"
									key={button.system.id}
									{...context}
								/>
							))}
						</div>
					) : null}
				</HeaderCard>
			</FullscreenVideo>
		</div>
	)
}
